"use client"

import Link from "next/link"
import { useEffect, useState } from "react"
import { TranslatedContent } from '@/shared/i18n/provider'

const PLANE_PATH =
  "M55.0293 0.549805L108.337 31.9062H55.3291L2.01953 0.549805H55.0293Z"

const loadingPhases = [
  {
    key: "collect",
    status: "후기를 모으는 중..",
    holdMs: 500,
    transitionMs: 420,
    planes: [
      { y: 32, opacity: 0.18 },
      { y: 32, opacity: 0.55 },
      { y: 32, opacity: 0.18 },
    ],
  },
  {
    key: "sort",
    status: "확인하는 중..",
    holdMs: 800,
    transitionMs: 380,
    planes: [
      { y: 14.4, opacity: 0.45 },
      { y: 32, opacity: 0.8 },
      { y: 50.7, opacity: 0.45 },
    ],
  },
  {
    key: "spread",
    status: "후기를 모으는 중..",
    holdMs: 1_200,
    transitionMs: 300,
    planes: [
      { y: 0, opacity: 1 },
      { y: 32, opacity: 1 },
      { y: 65, opacity: 1 },
    ],
  },
  {
    key: "verify",
    status: "확인하는 중..",
    holdMs: 800,
    transitionMs: 420,
    planes: [
      { y: 5.76, opacity: 0.72 },
      { y: 32, opacity: 1 },
      { y: 59.61, opacity: 0.72 },
    ],
  },
] as const

interface PlaneStackState {
  phaseIndex: number
  transitionMs: number
}

function PlaneStackLoader() {
  const [state, setState] = useState<PlaneStackState>({ phaseIndex: 0, transitionMs: 0 })
  const [reducedMotion, setReducedMotion] = useState(false)
  const phase = loadingPhases[state.phaseIndex]

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)")
    if (!media) return

    const updatePreference = () => setReducedMotion(media.matches)
    updatePreference()
    media.addEventListener?.("change", updatePreference)

    return () => media.removeEventListener?.("change", updatePreference)
  }, [])

  useEffect(() => {
    if (reducedMotion) return

    const timer = window.setTimeout(() => {
      setState((current) => {
        const currentPhase = loadingPhases[current.phaseIndex]
        return {
          phaseIndex: (current.phaseIndex + 1) % loadingPhases.length,
          transitionMs: currentPhase.transitionMs,
        }
      })
    }, state.transitionMs + phase.holdMs)

    return () => window.clearTimeout(timer)
  }, [phase.holdMs, reducedMotion, state.transitionMs])

  return (
    <TranslatedContent><div
      className="flex h-[140px] w-[118px] shrink-0 flex-col items-center gap-[19px] overflow-hidden rounded-[8px]"
      data-node-id="6291:9985"
      data-phase={phase.key}
      data-testid="treatment-plane-loader"
      role="status"
      aria-live="polite"
      aria-label={phase.status}
    >
      <svg
        className="h-[104px] w-[118px] shrink-0 overflow-visible"
        viewBox="0 0 118 104"
        fill="none"
        aria-hidden="true"
      >
        {phase.planes.map((plane, index) => (
          <path
            key={index}
            className="treatment-plane-loader__plane"
            d={PLANE_PATH}
            stroke="white"
            strokeOpacity="0.42"
            strokeWidth="1.1"
            style={{
              opacity: plane.opacity,
              transform: `translateY(${plane.y}px)`,
              transitionDuration: reducedMotion ? "0ms" : `${state.transitionMs}ms`,
            }}
          />
        ))}
      </svg>
      <p className="whitespace-nowrap text-center text-[14px] font-medium leading-[normal] text-[#efefef]">
        {phase.status}
      </p>
    </div></TranslatedContent>
  )
}

interface TreatmentLoadingScreenProps {
  treatmentName: string
}

/** Figma 6294:3495 — 울쎄라 외 시술의 공통 준비 중 화면. */
export function TreatmentLoadingScreen({ treatmentName }: TreatmentLoadingScreenProps) {
  return (
    <TranslatedContent><main
      className="relative min-h-[680px] w-full overflow-hidden bg-surface-base [height:100dvh]"
      data-node-id="6294:3495"
      aria-label={`${treatmentName} 준비 중`}
    >
      <div className="absolute left-1/2 top-[calc(50%_-_138px)] flex w-[calc(100%_-_64px)] max-w-[326px] -translate-x-1/2 flex-col items-center gap-[120px]">
        <PlaneStackLoader />

        <section className="flex w-full flex-col items-start gap-[28px]">
          <h1
            className="w-full text-center text-[15px] font-medium leading-[1.6] text-[#b8b4ac]"
            aria-label="아직 준비중이에요. 지금은 시술 27개까지 정리해뒀어요."
          >
            아직 준비중이에요.
            <br />
            지금은 시술 27개까지 정리해뒀어요.
          </h1>
          <Link
            href="/#treatments"
            className="flex h-[48px] w-full items-center justify-center rounded-[100px] bg-[#efece6] text-[14.5px] font-bold leading-[normal] tracking-[-0.3px] text-[#17150f] transition-colors hover:bg-[#e4e1da] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#efece6]"
            data-node-id="6294:3521"
          >
            다른 시술 보러 가기
          </Link>
        </section>
      </div>

      <p className="absolute bottom-[48px] left-1/2 w-[calc(100%_-_64px)] max-w-[326px] -translate-x-1/2 text-center text-[11.5px] font-normal leading-[1.4] text-[#827e77]">
        후기 100,000개 이상 모인 시술을 소개해요.
      </p>
    </main></TranslatedContent>
  )
}
